import { CERTIFICATIONS } from "@/data/catalogue";
import { ShieldCheck, Clock, FileDown, ArrowRight } from "lucide-react";

const DOWNLOADS = [
  { label: "Sample COA — Phycocyanin E25" },
  { label: "Technical Data Sheet (TDS) — All Grades" },
  { label: "Heavy Metals Panel — Lot Q4 2025" },
  { label: "Microbiology Report — E18 Food Grade" },
];

// Public-facing certification progress milestones. Update as targets move from
// Target → Application filed → Audit scheduled → Active.
const PROGRESS_LOG = [
  { date: "Q2 2026", cert: "FSSAI Central Licence", milestone: "Documentation package prepared · site + product schedule finalised" },
  { date: "Q2 2026", cert: "APEDA Registration", milestone: "Cultivation-facility export registration file being assembled" },
  { date: "Q3 2026", cert: "HACCP · ISO 22000:2018", milestone: "Process HACCP plan drafted; ISO 22000 pre-audit gap analysis initiated" },
  { date: "Q3 2026", cert: "NABL Lab Accreditation", milestone: "In-house analytical scope defined; NABL scope-of-accreditation being prepared" },
  { date: "Q4 2026", cert: "Halal (JUH-India)", milestone: "Formulation-side dossier drafted; audit calendar aligned to first production run" },
  { date: "Q4 2026", cert: "USDA Organic (NPOP) · EU Organic", milestone: "Certifier shortlisting under way; land-history and input-registers being compiled" },
  { date: "Q1 2027", cert: "WHO-GMP (E30/E40 line)", milestone: "Pharma-grade line design under review; qualification protocols being drafted" },
  { date: "Ongoing", cert: "IEC (DGFT)", milestone: "Applied for at incorporation; renewal cadence established" },
];

const Certifications = () => {
  return (
    <div data-testid="page-certifications" className="pt-[72px]">
      <section className="container-wide py-20 md:py-28">
        <p className="eyebrow mb-5">◦  Certifications & Compliance</p>
        <h1 className="font-display text-5xl md:text-6xl text-brand-black tracking-tight leading-[1.05] max-w-4xl">
          A documented case for <span className="italic text-brand-teal">procurement confidence.</span>
        </h1>
        <p className="mt-6 text-brand-black/70 max-w-2xl leading-relaxed">
          Every certification below is a formally planned pre-launch target on our July 2027 readiness roadmap — mapped to the issuing authority and grade line it applies to. Request the current progress file as part of your reserve-allocation or technical review.
        </p>
      </section>

      <section className="bg-brand-cream border-y border-brand-green/15">
        <div className="container-wide py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-green/15 border border-brand-green/15">
            {CERTIFICATIONS.map((c) => {
              const active = c.status === "Active";
              return (
                <div key={c.name} data-testid={`cert-${c.name.replace(/\s+/g, '-')}`} className="bg-white p-6 hover:bg-brand-cream transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-10 h-10 border border-brand-green/30 flex items-center justify-center text-brand-teal">
                      {active ? <ShieldCheck size={18} /> : <Clock size={18} />}
                    </div>
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${active ? "border-brand-green/40 text-brand-green bg-brand-green/5" : "border-brand-gold/50 text-[#8a6e1f] bg-brand-gold/10"}`}>
                      {c.status}
                    </span>
                  </div>
                  <h3 className="font-display text-xl text-brand-black mt-5">{c.name}</h3>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45 mt-2">Category · {c.category}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Progress log — makes pre-launch momentum visible */}
      <section className="container-wide py-20 md:py-24" data-testid="cert-progress-log">
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-12 flex-wrap">
          <div>
            <p className="eyebrow mb-4">◦  Roadmap</p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight max-w-2xl">
              Certification <span className="italic text-brand-teal">progress log.</span>
            </h2>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand-black/55 max-w-xs">Live status updates as each target moves from application → audit → active.</p>
        </div>
        <div className="border-l-2 border-brand-gold/40 pl-6 md:pl-8 space-y-6 md:space-y-7">
          {PROGRESS_LOG.map((item, i) => (
            <div key={i} data-testid={`cert-progress-item-${i}`} className="relative">
              <span className="absolute -left-[34px] md:-left-[42px] top-2 w-3 h-3 rounded-full bg-brand-gold ring-4 ring-brand-cream" />
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-teal">{item.date}</p>
              <h3 className="font-display text-lg md:text-xl text-brand-black mt-1">{item.cert}</h3>
              <p className="text-sm text-brand-black/70 mt-1.5 leading-relaxed max-w-2xl">{item.milestone}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-xs text-brand-black/50 font-mono uppercase tracking-[0.18em] max-w-2xl leading-relaxed">
          <ArrowRight size={12} className="inline mr-1" /> Buyers on the reserve-allocation list receive the full progress file (audit dates, certifier names, gap-analysis outputs) via export@spiruva.com.
        </p>
      </section>

      <section className="container-wide pb-24">
        <p className="eyebrow mb-4">◦  Document Downloads</p>
        <h2 className="font-display text-4xl md:text-5xl text-brand-black leading-tight">
          Sample documentation, <span className="italic text-brand-teal">on demand.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
          {DOWNLOADS.map((d) => (
            <button
              key={d.label}
              data-testid={`cert-download-${d.label.replace(/[^a-z0-9]+/gi, '-')}`}
              onClick={() => window.alert(`${d.label} — placeholder. Submit a sample request to receive the latest document via export@spiruva.com.`)}
              className="flex items-center justify-between bg-white border border-brand-green/15 p-5 hover:border-brand-gold transition group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-brand-cream border border-brand-green/15 flex items-center justify-center text-brand-teal group-hover:bg-brand-teal group-hover:text-brand-cream transition">
                  <FileDown size={16} />
                </div>
                <span className="text-sm text-brand-black font-medium">{d.label}</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40 group-hover:text-brand-gold transition">PDF</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Certifications;
